import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { ContentBlockType } from '@onesource/schemas';
import DOMPurify from 'dompurify';
import { MdFolder } from 'react-icons/md';
import { ProgressCard } from '#/features/community-hub/components/progress-card';
import { QuizCard } from '#/features/community-hub/components/quiz-card';
import { DocumentCard } from './document-card';
import { EmbedCard } from './embed-card';
import { FormCard } from './form-card';
import { GcsUploadCard } from './gcs-upload-card';
import { LessonContentSummary } from './lesson-content-summary';
import { RedirectCard } from './redirect-card';
import { SubLessonsSection } from './sub-lessons-section';
import { TextCard } from './text-card';
import type { LessonDetailViewProps } from './types';
import { VideoCard } from './video-card';
import { richHtmlSx } from '@onesource/components';

export function LessonDetailView({
  lesson,
  progressPercent,
  breadcrumbs,
  onNavigateToBlock,
  onNavigateToSubLesson,
}: LessonDetailViewProps) {
  const theme = useTheme();

  const showProgressBar = lesson.showProgressBar !== false;

  const hasSubLessons = (lesson.subLessonCount || 0) > 0 || (lesson.children && lesson.children.length > 0);

  const sortedBlocks = [...(lesson.contentBlocks || [])]
    .sort((a, b) => a.position - b.position)
    // Hide FORM blocks whose linked form isn't published; admins still see
    // them via the dashboard editor route.
    .filter(b => b.blockType !== ContentBlockType.FORM || b.formStatus === 'published');
  const videoBlocks = sortedBlocks.filter(b => b.blockType === ContentBlockType.VIMEO);
  const documentBlocks = sortedBlocks.filter(
    b =>
      b.blockType === ContentBlockType.PDF ||
      b.blockType === ContentBlockType.TEXT ||
      b.blockType === ContentBlockType.REDIRECT ||
      b.blockType === ContentBlockType.EMBED ||
      b.blockType === ContentBlockType.FORM ||
      b.blockType === ContentBlockType.GCS_UPLOAD,
  );

  const quizBlocks = sortedBlocks.filter(b => b.blockType === ContentBlockType.QUIZ);

  const videoSectionHeading = lesson.videoSectionTitle?.trim() || 'Video/s';
  const documentSectionHeading = lesson.documentSectionTitle?.trim() || 'Documents';
  const quizSectionHeading = lesson.quizSectionTitle?.trim() || 'Quiz';

  return (
    <Box>
      <Box>
        {breadcrumbs}

        <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '1 1 631px' },
              maxWidth: { xs: '100%', md: 631 },
              bgcolor: 'white',
              borderRadius: '15px',
              p: 2,
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <Typography variant="h5" sx={{ fontSize: '16px', fontWeight: 'bold', color: theme.palette.common.black }}>
                {lesson.title}
              </Typography>
              {hasSubLessons && (
                <Chip
                  icon={<MdFolder size={16} />}
                  label={`${lesson.subLessonCount || lesson.children?.length || 0} sub-lessons`}
                  size="small"
                  color="secondary"
                  variant="filled"
                />
              )}
            </Box>

            <Typography
              variant="body2"
              component="div"
              sx={{
                ...richHtmlSx,
                color: theme.palette.common.black,
                lineHeight: 1.6,
                fontSize: '14px',
                whiteSpace: 'pre-line',
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(lesson.longDescription || 'No description available'),
              }}
            />
          </Box>

          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '0 0 368px' },
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <LessonContentSummary
              lesson={lesson}
              hasSubLessons={!!hasSubLessons}
              videoBlocks={videoBlocks}
              documentBlocks={documentBlocks}
              quizBlocks={quizBlocks}
              onNavigateToBlock={onNavigateToBlock}
            />

            {showProgressBar && <ProgressCard progressPercent={progressPercent} />}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 4, width: { xs: '100%', md: 'calc(100% - 190px)' } }} />

      {hasSubLessons && <SubLessonsSection parentId={lesson.id} onNavigateToSubLesson={onNavigateToSubLesson} />}

      {hasSubLessons && <Divider sx={{ my: 4, width: { xs: '100%', md: 'calc(100% - 190px)' } }} />}

      {!hasSubLessons && videoBlocks.length > 0 && (
        <>
          <Box>
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', fontSize: '16px', color: theme.palette.common.black, mb: 1.5 }}
            >
              {videoSectionHeading}
            </Typography>
            <Box sx={{ py: 1, display: 'flex', gap: 3, overflowX: 'auto' }}>
              {videoBlocks.map(block => (
                <VideoCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />
              ))}
            </Box>
          </Box>
          <Divider sx={{ my: 4, width: { xs: '100%', md: 'calc(100% - 190px)' } }} />
        </>
      )}

      {!hasSubLessons && documentBlocks.length > 0 && (
        <>
          <Box
            pt={3}
            pb={4}
            sx={{
              backgroundColor: theme.palette.common.white,
              width: { xs: '100%', md: 'calc(100% - 190px)' },
              borderRadius: '15px',
              p: 3,
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', fontSize: '16px', color: theme.palette.common.black, mb: 1.5 }}
            >
              {documentSectionHeading}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2.25, flexWrap: 'wrap' }}>
              {documentBlocks.map(block => {
                if (block.blockType === ContentBlockType.PDF) {
                  return <DocumentCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />;
                }
                if (block.blockType === ContentBlockType.REDIRECT) {
                  return <RedirectCard key={block.id} block={block} />;
                }
                if (block.blockType === ContentBlockType.EMBED) {
                  return <EmbedCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />;
                }
                if (block.blockType === ContentBlockType.FORM) {
                  return <FormCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />;
                }
                if (block.blockType === ContentBlockType.GCS_UPLOAD) {
                  return <GcsUploadCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />;
                }
                return <TextCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />;
              })}
            </Box>
          </Box>
          <Divider sx={{ my: 4, width: { xs: '100%', md: 'calc(100% - 190px)' } }} />
        </>
      )}

      {!hasSubLessons && quizBlocks.length > 0 && (
        <Box pb={4}>
          <Typography
            variant="body1"
            sx={{ fontWeight: 'bold', fontSize: '16px', color: theme.palette.common.black, mb: 1.5 }}
          >
            {quizSectionHeading}
          </Typography>
          {quizBlocks.map(block => (
            <QuizCard
              key={block.id}
              title={block.title || 'Quiz'}
              quizId={block.quizId || ''}
              onClick={() => onNavigateToBlock(block)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
